import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '../../lib/trpc';

const EMPTY_RULES: RuleItem[] = [];

function haveSameIds(left: string[], right: string[]) {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

type Bill = {
  id: string;
  name: string;
};

type RuleItem = {
  id: string;
  pattern: string;
  categoryId: string;
  linkedBillId?: string | null;
  linkedBillName?: string | null;
};

type Props = {
  open: boolean;
  bill: Bill | null;
  onClose: () => void;
  onSaved: () => void;
};

export function BillForm({ open, bill, onClose, onSaved }: Props) {
  const utils = trpc.useUtils();
  const rulesQuery = trpc.categoryRule.list.useQuery(undefined, {
    enabled: open,
    retry: false,
  });
  const categoriesQuery = trpc.category.listGroups.useQuery(undefined, {
    enabled: open,
    retry: false,
  });

  const createMutation = trpc.recurringBill.create.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.recurringBill.list.invalidate(),
        utils.recurringBill.conflicts.invalidate(),
        utils.recurringBill.predict.invalidate(),
      ]);
      toast.success('Räkningen skapades');
      onSaved();
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = trpc.recurringBill.update.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.recurringBill.list.invalidate(),
        utils.recurringBill.conflicts.invalidate(),
        utils.recurringBill.predict.invalidate(),
        utils.recurringBill.history.invalidate(),
      ]);
      toast.success('Räkningen uppdaterades');
      onSaved();
    },
    onError: (error) => toast.error(error.message),
  });

  const [name, setName] = useState('');
  const [ruleSearch, setRuleSearch] = useState('');
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);
  const rules = (rulesQuery.data as RuleItem[] | undefined) ?? EMPTY_RULES;

  useEffect(() => {
    if (!open) {
      setName('');
      setRuleSearch('');
      setSelectedRuleIds([]);
      return;
    }

    setName(bill?.name ?? '');
    setRuleSearch('');
  }, [bill, open]);

  useEffect(() => {
    if (!open) return;

    if (!bill) {
      setSelectedRuleIds((current) => (current.length === 0 ? current : []));
      return;
    }

    const nextSelectedRuleIds = rules
      .filter((rule) => rule.linkedBillId === bill.id)
      .map((rule) => rule.id);

    setSelectedRuleIds((current) =>
      haveSameIds(current, nextSelectedRuleIds) ? current : nextSelectedRuleIds,
    );
  }, [bill, open, rules]);

  const categoryLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of categoriesQuery.data ?? []) {
      for (const category of group.categories) {
        map.set(category.id, `${group.name} / ${category.name}`);
      }
    }
    return map;
  }, [categoriesQuery.data]);

  const filteredRules = useMemo(() => {
    const searchValue = ruleSearch.trim().toLowerCase();
    if (!searchValue) return rules;

    return rules.filter((rule) => {
      const categoryLabel = categoryLabels.get(rule.categoryId)?.toLowerCase() ?? '';
      return (
        rule.pattern.toLowerCase().includes(searchValue) || categoryLabel.includes(searchValue)
      );
    });
  }, [categoryLabels, ruleSearch, rules]);

  const handleToggleRule = (ruleId: string) => {
    setSelectedRuleIds((current) =>
      current.includes(ruleId) ? current.filter((id) => id !== ruleId) : [...current, ruleId],
    );
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (selectedRuleIds.length === 0) {
      toast.error('Välj minst en regel för att koppla räkningen');
      return;
    }

    const payload = {
      name: name.trim(),
      categoryRuleIds: selectedRuleIds,
    };

    if (bill) {
      updateMutation.mutate({ id: bill.id, ...payload });
      return;
    }

    createMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{bill ? 'Redigera räkning' : 'Skapa räkning'}</DialogTitle>
          <DialogDescription>
            Koppla en eller flera kategoriregler till räkningen så att importerade transaktioner kan
            matchas automatiskt.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="bill-name">Namn</Label>
            <Input
              id="bill-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-1"
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="bill-rule-search">Kopplade regler</Label>
              <span className="text-xs text-muted-foreground">{selectedRuleIds.length} valda</span>
            </div>
            <Input
              id="bill-rule-search"
              value={ruleSearch}
              onChange={(event) => setRuleSearch(event.target.value)}
              placeholder="Sök på regel eller kategori"
            />
            <div className="relative max-h-72 space-y-2 overflow-y-auto rounded-xl border border-border p-3">
              {rules.length === 0 ? (
                <p className="text-sm text-muted-foreground">Inga kategoriregler finns ännu.</p>
              ) : filteredRules.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen regel matchar din sökning.</p>
              ) : (
                filteredRules.map((rule) => {
                  const disabled = Boolean(rule.linkedBillId) && rule.linkedBillId !== bill?.id;

                  return (
                    <div
                      key={rule.id}
                      className={`flex items-start gap-3 rounded-lg border px-3 py-3 ${disabled ? 'border-dashed bg-muted/30 opacity-60' : 'border-border'}`}
                    >
                      <Checkbox
                        aria-label={`Koppla regel ${rule.pattern}`}
                        checked={selectedRuleIds.includes(rule.id)}
                        onCheckedChange={() => handleToggleRule(rule.id)}
                        disabled={disabled}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm text-foreground">{rule.pattern}</div>
                        <div className="text-xs text-muted-foreground">
                          {categoryLabels.get(rule.categoryId) ?? rule.categoryId}
                        </div>
                        {disabled && (
                          <div className="mt-1 text-xs text-warning">
                            Redan kopplad till "{rule.linkedBillName}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending
                ? 'Sparar…'
                : bill
                  ? 'Spara ändringar'
                  : 'Skapa räkning'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
